/**
 * YUMIX — Day7ChallengeIntro
 *
 * Mandatory full-screen splash shown right after onboarding completion,
 * before the user reaches the Dashboard for the first time.
 *
 * Single CTA "Accepter le Défi" → POST /welcome-challenge/accept-intro
 * → marks `welcome_intro_accepted=true` on the user doc → navigate /dashboard.
 *
 * No skip option. Accepting the challenge IS the entry into the app.
 *
 * Re-route guard : if `welcome_intro_accepted=true` already, redirect
 * straight to /dashboard (in case the user lands here by URL).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth, api } from '../context/AppContext';
import { Button } from '../components/ui/button';
import {
  Flame, Award, Calendar, Utensils, Activity,
  TrendingUp, Loader2, Sparkles, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

const Day7ChallengeIntro = () => {
  const { user, language, updateUser } = useAuth();
  const navigate = useNavigate();
  const [accepting, setAccepting] = useState(false);
  const fr = (language || 'fr').startsWith('fr');

  // Guard — if already accepted, route to dashboard
  useEffect(() => {
    if (user?.welcome_intro_accepted) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const handleAccept = async () => {
    setAccepting(true);
    try {
      await api.post('/welcome-challenge/accept-intro');
      // Optimistic local update so the guard doesn't bounce us back
      try { updateUser?.({ ...user, welcome_intro_accepted: true }); } catch { /* silent */ }
      toast.success(fr ? 'Défi accepté · Bienvenue dans la meute' : 'Challenge accepted · Welcome to the pack');
      navigate('/dashboard', { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || (fr ? 'Erreur · réessaie' : 'Error · retry'));
      setAccepting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background relative overflow-hidden px-4 py-8">
      {/* Background ambient glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-orange-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-0 w-[300px] h-[300px] bg-amber-500/15 rounded-full blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-md mx-auto z-10"
        data-testid="day7-intro-screen"
      >
        {/* Hero badge */}
        <motion.div
          initial={{ scale: 0.7, rotate: -10 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: 'spring', stiffness: 200, damping: 14, delay: 0.1 }}
          className="text-center mb-6"
        >
          <div className="inline-flex items-center justify-center w-24 h-24 rounded-3xl bg-gradient-to-br from-orange-500 via-orange-600 to-amber-500 shadow-2xl shadow-orange-500/40 mb-4">
            <Flame className="w-12 h-12 text-white" />
          </div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-orange-400 font-bold">
            {fr ? 'Première mission' : 'First mission'}
          </p>
          <h1 className="font-heading text-3xl sm:text-4xl uppercase tracking-tight text-foreground mt-2 leading-tight">
            {fr ? 'Le Défi 7 Jours' : 'The 7-Day Challenge'}
          </h1>
        </motion.div>

        {/* Welcome paragraph */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="rounded-2xl border border-orange-500/30 bg-card/60 backdrop-blur-md p-5 mb-4"
        >
          <p className="text-sm leading-relaxed text-foreground/90">
            {fr
              ? <>Bienvenue dans la meute <strong className="text-orange-400">YUMIX</strong>, {user?.name?.split(' ')[0] || 'soldat'}. Ton premier objectif : <strong>le Défi 7 Jours</strong>.</>
              : <>Welcome to the <strong className="text-orange-400">YUMIX</strong> pack, {user?.name?.split(' ')[0] || 'soldier'}. Your first objective : <strong>the 7-Day Challenge</strong>.</>}
          </p>
          <p className="text-sm leading-relaxed text-foreground/85 mt-2">
            {fr
              ? <>Logue tes repas et tes entraînements avec <strong>constance</strong> pendant une semaine pour débloquer ton <strong className="text-orange-400">premier Grade officiel</strong>.</>
              : <>Log your meals and workouts with <strong>consistency</strong> for one week to unlock your <strong className="text-orange-400">first official Grade</strong>.</>}
          </p>
        </motion.div>

        {/* The 3 rules */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55 }}
          className="space-y-2 mb-5"
        >
          <RuleRow
            icon={Utensils}
            title={fr ? 'Logue tes repas' : 'Log your meals'}
            desc={fr ? 'Chaque repas compte. Photo ou texte, peu importe.' : 'Every meal counts. Photo or text, doesn\'t matter.'}
          />
          <RuleRow
            icon={Activity}
            title={fr ? 'Logue tes entraînements' : 'Log your workouts'}
            desc={fr ? 'Marche, gym, étirement — tout est valide.' : 'Walk, gym, stretching — all valid.'}
          />
          <RuleRow
            icon={TrendingUp}
            title={fr ? 'Vise la constance' : 'Aim for consistency'}
            desc={fr ? '7 jours d\'affilée. Pas la perfection, la régularité.' : '7 days in a row. Not perfection, regularity.'}
          />
        </motion.div>

        {/* Reward preview */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-4 mb-6 flex items-center gap-3"
        >
          <Award className="w-8 h-8 text-amber-400 shrink-0" />
          <div className="text-xs">
            <p className="font-bold text-amber-300 uppercase tracking-wider">
              {fr ? 'Récompense Jour 7' : 'Day 7 reward'}
            </p>
            <p className="text-foreground/80 mt-0.5">
              {fr
                ? 'Bilan de performance + ton premier Grade débloqué.'
                : 'Performance bilan + your first Grade unlocked.'}
            </p>
          </div>
        </motion.div>

        {/* CTA — single, mandatory entry point */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85 }}
        >
          <Button
            onClick={handleAccept}
            disabled={accepting}
            className="w-full h-14 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-heading uppercase tracking-[0.15em] text-sm shadow-xl shadow-orange-500/30"
            data-testid="day7-intro-accept-btn"
          >
            {accepting ? (
              <><Loader2 className="w-5 h-5 mr-2 animate-spin" />{fr ? 'Activation...' : 'Activating...'}</>
            ) : (
              <>
                <Flame className="w-5 h-5 mr-2" />
                {fr ? 'Accepter le défi' : 'Accept the challenge'}
                <ChevronRight className="w-5 h-5 ml-2" />
              </>
            )}
          </Button>
          <p className="text-center text-[10px] text-muted-foreground mt-3 flex items-center justify-center gap-1">
            <Sparkles className="w-3 h-3 text-orange-400" />
            {fr
              ? 'Accepter le défi est ton entrée dans l\'app. Allons-y.'
              : 'Accepting the challenge is your entry into the app. Let\'s go.'}
          </p>
        </motion.div>

        {/* Mini progress preview (1..7) */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="mt-6 flex items-center justify-center gap-1.5"
          aria-hidden
        >
          {[1,2,3,4,5,6,7].map(d => (
            <div key={d} className="flex flex-col items-center gap-1">
              <Calendar className="w-3 h-3 text-muted-foreground/60" />
              <span className="text-[9px] text-muted-foreground tabular-nums">J{d}</span>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </div>
  );
};

const RuleRow = ({ icon: Icon, title, desc }) => (
  <div className="flex items-start gap-3 rounded-lg border border-border/40 bg-secondary/30 px-3 py-2.5">
    <div className="shrink-0 w-8 h-8 rounded-md bg-orange-500/15 flex items-center justify-center">
      <Icon className="w-4 h-4 text-orange-400" />
    </div>
    <div className="text-xs">
      <p className="font-bold text-foreground">{title}</p>
      <p className="text-muted-foreground mt-0.5">{desc}</p>
    </div>
  </div>
);

export default Day7ChallengeIntro;
